import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import { ITableProps } from '../../../Table/models/index.js';
import { ITableRow } from '../../../Table/models/ITableRow.js';
import { ITableGrouping } from '../../models/ITableGrouping.js';
import { ITableGroupingToggle } from '../../models/ITableGroupingToggle.js';
import { ToggleState } from '../../models/ToggleState.js';
import GroupBody from '../GroupBody/GroupBody.js';
import GroupHeader from '../GroupHeader/GroupHeader.js';

import { IGroupConfiguration, IGroupProps, IGroupState } from './models/index.js';
import { GROUP_CATEGORY_KEY, GROUP_COMPONENT_KEY, GROUP_PARENT_COMPONENT_KEY } from './Group.meta.js';

export class Group extends ComponentBase<IGroupProps, IGroupState, IGroupConfiguration> {
	categoryKey = GROUP_CATEGORY_KEY;
	componentKey = GROUP_COMPONENT_KEY;
	get parentComponentKey(): string | undefined {
		return GROUP_PARENT_COMPONENT_KEY;
	}

	updated = 0;

	constructor(props: IGroupProps) {
		super(props);

		makeObservable(this, {
			updated: observable,
			selected: observable,
			toggleState: computed,
			tableProps: computed,
			childGrouping: computed,
			rows: computed,
			toggle: computed,
			handleToggle: action,
			handleSelectionChange: action,
		});
	}

	get toggle(): ITableGroupingToggle | undefined {
		return this.props.grouping.toggle;
	}

	get tableProps(): ITableProps {
		return {
			...this.props.grouping.tableProps,
			rows: this.rows,
		};
	}

	get childGrouping(): ITableGrouping | undefined {
		return this.props.grouping.childGrouping;
	}

	get rows(): ITableRow[] {
		const { rows = [] } = this.props;
		const { key } = this.props.grouping;
		return rows.filter((row) => row[key] === this.props.value);
	}

	get toggleState(): ToggleState {
		if (this.props.group.toggleState !== undefined) {
			return this.props.group.toggleState;
		}
		if (this.props.grouping.toggle?.enabled && this.props.grouping.toggle.defaultValue !== undefined) {
			return this.props.grouping.toggle.defaultValue;
		}
		return ToggleState.expanded;
	}

	selected = false;

	handleToggle = (toggleState: ToggleState) => {
		if (this.toggle && this.toggle.onToggleChange) {
			this.toggle.onToggleChange(toggleState, this.props.group.treeIndex);
		}
	};

	handleSelectionChange = (value: boolean) => {
		if (this.props.grouping.onGroupSelectionChange) {
			this.props.grouping.onGroupSelectionChange(value, this.rows);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	componentDidUpdate() {
		const allChildrenSelected = this.rows.every((row) => row._selected);
		if (allChildrenSelected !== this.selected) {
			this.selected = allChildrenSelected;
		}
	}

	render() {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { children, grouping, rows, configuration, group, value, ...otherProps } = this.props;
		return (
			<section {...otherProps}>
				<GroupHeader
					selected={this.selected}
					onSelectionChange={this.handleSelectionChange}
					grouping={grouping}
					rows={this.rows}
					value={value}
					toggleState={this.toggleState}
					onToggle={this.handleToggle}
					suppressAnalytics={true}
				/>
				{this.toggleState === ToggleState.expanded && (
					<GroupBody grouping={grouping} group={group} rows={this.rows} />
				)}
			</section>
		);
	}
}

export default InjectedStyledComponent(observer(Group), GROUP_CATEGORY_KEY, GROUP_PARENT_COMPONENT_KEY, [
	GROUP_COMPONENT_KEY,
]);
